/**
 * A short-lived, positive "this user just submitted a scan" marker.
 *
 * Why this exists — and why it is NOT `scanID`:
 *
 * On success `ScanUploader.reset()` deletes `localStorage.scanID`/`timestamp`,
 * which is correct bookkeeping for a finished scan. But `NewAvatarScreen`
 * derives its Step-1 completion check by reading `scanID` directly, so every
 * arrival at `/new-avatar-screen` after a success — Android back gesture,
 * refresh, new tab — renders Step 1 as never-started and the user concludes the
 * scan was lost. Deterministic, not intermittent. This marker is the missing
 * signal: the keys are gone *because* the scan succeeded, and something has to
 * say so.
 *
 * It must never be `scanID`/`timestamp` and must never revive them. Those keys
 * are consumed by `ScanUploader`, `scanRecovery`, `ScanModel.create`'s
 * `storagePath`, the AvatarScreen/AvatarSelect synthetic "Submitting Scans ..."
 * card, `NewAvatarScreen`'s `validate()`, and the discard modals — putting a
 * value back into them after a success resurrects the eternal
 * "Submitting Scans ..." bug (incident 2026-07-24). Hence a separate key with a
 * distinct name, read by nothing else.
 *
 * The mirror image of this module is `scanRecovery.ts`, which handles keys that
 * WRONGLY REMAIN; it opens with `if (!scanID) return null;` and so cannot see
 * this case at all. See HERE_BE_DRAGONS § "The mirror image: after a SUCCESS
 * the keys are GONE".
 */

/** Distinct from `scanID`/`timestamp` on purpose — see the note above. */
const SCAN_SUBMITTED_MARKER_KEY = "lastSubmittedScan";

/**
 * The scanId of the submission the user has already been told about.
 *
 * The marker answers "did a scan just succeed?". That is the right question
 * for the Submit guard's *first* half and for the `msSinceLastSuccess`
 * measurement, but it is the wrong question for the UI, which should only
 * reassure a user who does not already know. A user who went to their avatars
 * list — where the scan is visible as Processing — and pressed "New Avatar"
 * has said, with the press, that they know: they are deliberately starting
 * ANOTHER scan, and telling them "Submitted" over a form they opened on
 * purpose is noise. A user who arrived by a stray back gesture, a reload or a
 * redirect-flow exit has said nothing of the kind, and still needs the line.
 *
 * A SEPARATE key rather than clearing the marker, because the marker has two
 * other consumers that must not be disturbed: the mount log's
 * `msSinceLastSuccess` (clearing it would make the acknowledged arrivals
 * invisible and silently change what the rate is measuring — the BUG-0003
 * mistake), and the TTL that bounds the whole thing. Storing the scanId rather
 * than a boolean makes the acknowledgement per-submission: the next success
 * overwrites the marker with a different scanId, which no longer matches, so
 * the new submission starts unacknowledged with no extra bookkeeping.
 *
 * Stored as `{ scanId, at }` so the acknowledgement can expire on its OWN
 * clock — see `SCAN_SUBMITTED_ACK_TTL_MS`.
 */
const SCAN_SUBMITTED_ACK_KEY = "lastSubmittedScanAck";

/**
 * How long one deliberate press stands the reassurance down.
 *
 * Deliberately much shorter than the marker's own TTL, and NOT inherited from
 * it. The press proves the user knew *at that moment*; the reassurance exists
 * for a moment of doubt *later*. Letting one press silence Step 1 for the
 * marker's full 30 minutes would put every later arrival — the stray back
 * gesture, the reload, the new tab that BUG-0003 was written for — back into
 * the original bug, having been "told" once, twenty minutes ago.
 *
 * Two minutes covers what the press actually created: the transition to the
 * form, a hard reload of it, and an iOS permission-prompt round trip. It
 * leaves at least eight of the ten minutes in which 36% of "Incomplete Steps"
 * banners land on users whose scan had already succeeded (HERE_BE_DRAGONS §
 * "The mirror image") covered by the reassurance.
 *
 * The asymmetry is what picks the short value: an ack that expires too early
 * shows the user a true statement they already knew, while one that lives too
 * long lets them conclude their scan was lost. Erring short errs toward the
 * `[2.5.4]` behaviour.
 *
 * NOTE: this bounds when a NEW arrival at the form counts as a fresh one, not
 * how long the user may sit on the form. `NewAvatarScreen` freezes the
 * acknowledged decision at mount precisely so this TTL can be short without
 * the Step-1 row flipping under a user who is still filling the form.
 */
export const SCAN_SUBMITTED_ACK_TTL_MS = 2 * 60 * 1000;

/**
 * How long a marker stays live.
 *
 * It is bounding a UI state ("your scan is processing"), not a durable fact, so
 * it only has to outlast the window in which the user might wander back to the
 * form. Prod puts that window in minutes: the resubmit signature counts presses
 * 0–600s after a success, and 36% of "Incomplete Steps" banners land within ten
 * minutes of that same user's success. Thirty minutes is comfortably past that
 * while still expiring on its own if anything else goes wrong.
 */
export const SCAN_SUBMITTED_MARKER_TTL_MS = 30 * 60 * 1000;

export interface ScanSubmittedMarker {
  /** The scan that was submitted — so a caller can tell WHICH scan it was. */
  scanId: string;
  /** Epoch ms at which the submission succeeded. */
  submittedAt: number;
  /** Age at read time. Feeds the `msSinceLastSuccess` instrumentation. */
  ageMs: number;
  /**
   * True while the user has demonstrably been told: they pressed a deliberate
   * "start another scan" control naming this submission, within the last
   * `SCAN_SUBMITTED_ACK_TTL_MS`. The marker is still live — only the
   * reassurance UI stands down, and only for that window. See
   * `acknowledgeScanSubmitted` and `SCAN_SUBMITTED_ACK_TTL_MS`.
   */
  acknowledged: boolean;
}

/**
 * Record a successful submission. Call this from the finalize SUCCESS path
 * only — never from `reset()`, which also runs on rotation reset, discard and
 * upload failure, where a marker would tell the user a scan succeeded when it
 * did not.
 */
export const markScanSubmitted = (scanId: string): void => {
  if (!scanId) return;
  try {
    localStorage.setItem(
      SCAN_SUBMITTED_MARKER_KEY,
      JSON.stringify({ scanId, submittedAt: Date.now() }),
    );
  } catch {
    // Quota / private-mode failures are non-fatal: with no marker the UI simply
    // falls back to its pre-fix behaviour.
  }
};

/**
 * Whether `scanId` carries a live acknowledgement.
 *
 * EVERY failure mode — unreadable storage, absent, malformed, naming another
 * scan, expired, or stamped in the future — returns `false`, i.e. "not
 * acknowledged", i.e. show the reassurance. That is the `[2.5.4]` behaviour,
 * so this whole mechanism degrades back to the fix it narrows rather than past
 * it into the bug.
 */
const isAcknowledged = (scanId: string): boolean => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(SCAN_SUBMITTED_ACK_KEY);
  } catch {
    return false;
  }
  if (!raw) return false;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return false;
  }
  if (!parsed || typeof parsed !== "object") return false;

  const { scanId: ackScanId, at } = parsed as {
    scanId?: unknown;
    at?: unknown;
  };
  // Names a different submission — a leftover ack can never suppress the line
  // for a scan it does not name.
  if (typeof ackScanId !== "string" || ackScanId !== scanId) return false;
  if (typeof at !== "number" || Number.isNaN(at)) return false;

  const ageMs = Date.now() - at;
  // Same call the marker makes about its own stamp: a backwards device clock
  // is distrusted rather than treated as fresh.
  if (ageMs < 0) return false;
  return ageMs < SCAN_SUBMITTED_ACK_TTL_MS;
};

/**
 * The live marker, or null when there is none, it has expired, or the stored
 * value is unusable.
 *
 * Deliberately free of side effects — the Step-1 JSX calls this during render,
 * so it must not write to localStorage. An expired marker is reported as
 * absent and left in place; the next success overwrites it and the next scan
 * start clears it.
 */
export const readScanSubmittedMarker = (): ScanSubmittedMarker | null => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(SCAN_SUBMITTED_MARKER_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;

  const { scanId, submittedAt } = parsed as {
    scanId?: unknown;
    submittedAt?: unknown;
  };
  if (typeof scanId !== "string" || !scanId) return null;
  if (typeof submittedAt !== "number" || Number.isNaN(submittedAt)) return null;

  const ageMs = Date.now() - submittedAt;
  // A negative age means the device clock moved backwards between the write and
  // this read. Distrust it rather than treat it as fresh — same call
  // scanRecovery makes about its heartbeat stamp — so the worst case is the
  // pre-fix behaviour rather than a state that outstays its welcome.
  if (ageMs < 0) return null;
  if (ageMs >= SCAN_SUBMITTED_MARKER_TTL_MS) return null;

  return {
    scanId,
    submittedAt,
    ageMs,
    acknowledged: isAcknowledged(scanId),
  };
};

/**
 * Record that the user has been told about the live submission, if there is
 * one. Call this from controls that mean "I am deliberately starting another
 * scan" — the avatars list's New Avatar button, the share flow's equivalent,
 * and Redo Scan on the failure screen.
 *
 * A no-op when there is no live marker: with nothing to acknowledge, writing
 * a scanId here would be acknowledging a submission that has not happened yet.
 *
 * `expectedScanId` scopes the acknowledgement to a submission the CALLER can
 * name, for callers that are talking about one specific scan rather than
 * "whatever is live". Redo Scan is the case: it speaks for the scan whose
 * failure screen the user is looking at, and that is not necessarily the
 * marked one — a user who submitted scan B successfully and then opened the
 * older, failed scan A has been told about A and nothing about B. Passing the
 * id makes the mismatch a no-op, so B keeps its reassurance. Omit it where the
 * control genuinely means "whatever was last submitted" (the two avatar-list
 * buttons, which show the marked scan as Processing on the screen the user is
 * pressing from).
 */
export const acknowledgeScanSubmitted = (expectedScanId?: string): void => {
  const marker = readScanSubmittedMarker();
  if (!marker) return;
  if (expectedScanId && expectedScanId !== marker.scanId) return;
  try {
    localStorage.setItem(
      SCAN_SUBMITTED_ACK_KEY,
      JSON.stringify({ scanId: marker.scanId, at: Date.now() }),
    );
  } catch {
    // Non-fatal: without the ack the UI simply keeps showing "Submitted",
    // which is the behaviour this whole mechanism is narrowing, not breaking.
  }
};

/**
 * Drop the marker. Called when a new scan starts, so a user who wants a second
 * avatar is not held on "Submitted" for the rest of the TTL.
 */
export const clearScanSubmittedMarker = (): void => {
  try {
    localStorage.removeItem(SCAN_SUBMITTED_MARKER_KEY);
    // The ack is meaningless without the marker it points at, and leaving it
    // behind would be a stale scanId sitting in storage for no reason. It could
    // not cause a false acknowledgement — the next success writes a different
    // scanId — but it costs nothing to keep storage honest.
    localStorage.removeItem(SCAN_SUBMITTED_ACK_KEY);
  } catch {
    // Same as the write: nothing actionable, and the TTL bounds the worst case.
  }
};
